// @flow
import * as React from 'react';
import glamorous from 'glamorous';
import { size as boxSize } from 'polished';
import { FRIEND, YOU_REQUESTED } from 'app/constants/friendStatusTypes';
import crown from 'app/images/ui/icon-crown.svg';
import FixedDimensionRatio from 'app/components/commons/FixedDimensionRatio';
import FriendIcon from 'app/components/icons/FriendIcon';
import ResponsiveImage from 'app/components/commons/ResponsiveImage';
import { getAvatarImages } from 'app/utils/avatarUtils';

type Props = {
  avatar: Object,
  className?: string,
  countryFlag?: string,
  hasCrown?: boolean,
  hasWhiteBackground?: boolean,
  profileCompletePercentage?: number,
  size: 'xxsmall' | 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge',
  status?: number,
  square?: boolean,
};

function getWidthFromSize(size) {
  return {
    xxsmall: 32,
    xsmall: 40,
    small: 48,
    medium: 72,
    large: 108,
    xlarge: 144,
  }[size];
}

function getIconWidthFromSize(size) {
  return {
    xxsmall: 16,
    xsmall: 16,
    small: 20,
    medium: 25,
    large: 30,
    xlarge: 35,
  }[size];
}

const AvatarMain = glamorous.div(({ size, theme }) => ({
  display: 'block',
  maxWidth: '100%',
  position: 'relative',
  ...boxSize(getWidthFromSize(size)),
  backgroundColor: theme.color.basis5,
  borderRadius: '50%',
}));

const AvatarImage = glamorous(ResponsiveImage)(
  {
    height: 'auto',
    position: 'relative',
    width: '100%',
  },
  ({ profileCompletePercentage }) => ({
    filter: `grayscale(${100 - profileCompletePercentage}%)`,
  }),
  ({ square }) =>
    (square
      ? {
        borderRadius: 0,
      }
      : {
        borderRadius: '50%',
      }),
);

const FriendStatusIcon = glamorous(FriendIcon)(
  ({ status, theme }) => ({
    color: status === YOU_REQUESTED ? theme.color.basis5 : theme.color.primary,
    bottom: -2,
    position: 'absolute',
    left: -5,
    borderRadius: '50%',
    boxSizing: 'content-box',
  }),
  ({ whiteBorder }) =>
    whiteBorder && {
      border: '2px solid white',
    },
  ({ size }) => ({
    ...boxSize(getIconWidthFromSize(size)),
  }),
);

const AvatarFlagWrapper = glamorous.div(
  {
    bottom: -2,
    position: 'absolute',
    right: -5,
    borderRadius: '50%',
    boxSizing: 'content-box',
  },
  ({ whiteBorder }) =>
    whiteBorder && {
      border: '2px solid white',
    },
  ({ size }) => ({
    ...boxSize(getIconWidthFromSize(size)),
  }),
  ({ theme }) => ({
    backgroundColor: theme.color.basis5,
  }),
);

const AvatarFlagImage = glamorous.img({
  display: 'block',
  width: '100%',
  height: '100%',
  borderRadius: '50%',
});

const CrownImage = glamorous.img({
  width: '40%',
  height: '40%',
  position: 'absolute',
  top: '-23%',
  right: '-2%',
  transform: 'rotate(30deg)',
});

class Avatar extends React.Component<Props> {
  props: Props;

  static defaultProps = {
    className: '',
    countryFlag: undefined,
    hasCrown: false,
    hasWhiteBackground: false,
    profileCompletePercentage: 100,
    status: undefined,
    square: false,
  };

  render() {
    const {
      avatar,
      className,
      countryFlag,
      hasCrown,
      hasWhiteBackground,
      profileCompletePercentage,
      size,
      status,
      square,
    } = this.props;

    return (
      <AvatarMain size={size} className={className}>
        <FixedDimensionRatio ratio={1}>
          {avatar && (
            <AvatarImage
              alt="avatar"
              square={square}
              images={getAvatarImages(avatar)(size)}
              profileCompletePercentage={profileCompletePercentage}
            />
          )}
          {(status === FRIEND || status === YOU_REQUESTED) && (
            <FriendStatusIcon
              type="icon-friend-circle"
              size={size}
              status={status}
              whiteBorder={hasWhiteBackground}
            />
          )}
          {hasCrown && <CrownImage alt="crown" src={crown} />}
          {countryFlag && (
            <AvatarFlagWrapper whiteBorder={hasWhiteBackground} size={size}>
              <AvatarFlagImage src={countryFlag} alt="" />
            </AvatarFlagWrapper>
          )}
        </FixedDimensionRatio>
      </AvatarMain>
    );
  }
}

export default Avatar;
